// @flow
import { createAction } from 'redux-actions'
import { standardMETASet } from '../../util/redux'
// Types
import type { DateObject } from '../../util/time'
import type { ZoneModel } from './types'

export * from './crud'
export * from './methods'

/**
 * Exports zone redux module in `Ducks` format
 * @see {@link https://github.com/erikras/ducks-modular-redux}
 * @module ducks/zone
 */

// Types/Constants ////////////////////////////////////////////////////////////

export type ZoneStore = {|
  [zoneId: string]: ZoneModel
|}

/**
 * replace .META.schedules array for all zones when we get new data
 * @param {Array} schedules - new zone schedule data
 * @param {Object} oldState - current reducer state
 * @returns {Object} new reducer state with update schedules array
 */
/* \$FlowFixMe This comment suppresses an error found when upgrading Flow to
 * v0.85.0. To view the error, delete this comment and run Flow. */
export const applyZoneScheduleArrays = (schedules, oldState) => {
  const state = { ...oldState }
  schedules.forEach(({ id, summary, name, enabled, zoneInfo }) => {
    zoneInfo.forEach(({ zoneId }) => {
      const { META: { schedules = [] } = {}, META, ...zoneData } =
        state[zoneId] || {}

      state[zoneId] = {
        ...zoneData,
        META: {
          ...META,
          schedules: [
            ...schedules.filter(schedule => schedule.id !== id),
            { id, summary, name, enabled }
          ]
        }
      }
    })
  })
  return state
}

const formatWaterJournalDate = (dateObj: DateObject): string =>
  dateObj.year + '-' + dateObj.month + '-' + dateObj.day

// ACTION TYPES ///////////////////////////////////////////////////////////////

const ZONE_CALIBRATION_ENDED_EVENT = 'RACHIO/ZONE/CALIBRATION_ENDED_EVENT'
const ZONE_GET_INIT = 'RACHIO/ZONE/GET_INIT'
const ZONE_GET_COMPLETE = 'RACHIO/ZONE/GET_COMPLETE'
const ZONE_UPDATE_INIT = 'RACHIO/ZONE/UPDATE_INIT'
const ZONE_UPDATE_COMPLETE = 'RACHIO/ZONE/UPDATE_COMPLETE'
const DISMISS_ALERTS_INIT = 'DISMISS_ALERTS_INIT'
const DISMISS_ALERTS_COMPLETE = 'DISMISS_ALERTS_COMPLETE'
const UPDATE_ZONE_FLOW_INIT = 'UPDATE_ZONE_FLOW_INIT'
const UPDATE_ZONE_FLOW_COMPLETE = 'UPDATE_ZONE_FLOW_COMPLETE'
const SET = 'RACHIO/ZONE/SET'
const GET_WATER_JOURNAL_INIT = 'RACHIO/ZONE/GET_WATER_JOURNAL_INIT'
const GET_WATER_JOURNAL = 'RACHIO/ZONE/GET_WATER_JOURNAL'
const GET_WATER_JOURNAL_COMPLETE = 'RACHIO/ZONE/GET_WATER_JOURNAL_COMPLETE'
const SET_WATER_JOURNAL_INIT = 'RACHIO/ZONE/SET_WATER_JOURNAL_INIT'
const SET_WATER_JOURNAL = 'RACHIO/ZONE/SET_WATER_JOURNAL'
const SET_WATER_JOURNAL_COMPLETE = 'RACHIO/ZONE/SET_WATER_JOURNAL_COMPLETE'
const SET_STATE = 'RACHIO/ZONE/SET_STATE'
const GET_ZONE_DEFAULT_INIT = 'RACHIO/ZONE/GET_ZONE_DEFAULTS_INIT'
const GET_ZONE_DEFAULT_COMPLETE = 'RACHIO/ZONE/GET_ZONE_DEFAULTS_COMPLETE'
const START_CALIBRATION_INIT = 'RACHIO/ZONE/START_CALIBRATION_INIT'
const START_CALIBRATION_COMPLETE = 'RACHIO/ZONE/START_CALIBRATION_COMPLETE'
const STOP_CALIBRATION_INIT = 'RACHIO/ZONE/STOP_CALIBRATION_INIT'
const STOP_CALIBRATION_COMPLETE = 'RACHIO/ZONE/STOP_CALIBRATION_COMPLETE'
const UPDATE_ZONE_SCHEDULES = 'RACHIO/ZONE/UPDATE_ZONE_SCHEDULES'
const GET_WATERING_SUMMARY_INIT = 'RACHIO/ZONE/GET_WATERING_SUMMARY_INIT'
const GET_WATERING_SUMMARY_COMPLETE =
  'RACHIO/ZONE/GET_WATERING_SUMMARY_COMPLETE'
const GET_CSV_WATERING_SUMMARY_INIT =
  'RACHIO/ZONE/GET_CSV_WATERING_SUMMARY_INIT'
const GET_CSV_WATERING_SUMMARY_COMPLETE =
  'RACHIO/ZONE/GET_CSV_WATERING_SUMMARY_COMPLETE'

// ACTION CREATORS ////////////////////////////////////////////////////////////

export const zoneCalibrationEndedEvent = createAction(
  ZONE_CALIBRATION_ENDED_EVENT
)
export const zoneGetDefaultInit = createAction(GET_ZONE_DEFAULT_INIT)
export const zoneGetDefaultComplete = createAction(GET_ZONE_DEFAULT_COMPLETE)
export const zoneGetInit = createAction(ZONE_GET_INIT)
export const zoneGetComplete = createAction(ZONE_GET_COMPLETE)
export const zoneUpdateInit = createAction(ZONE_UPDATE_INIT)
export const zoneUpdateComplete = createAction(ZONE_UPDATE_COMPLETE)
export const dismissAlertsInit = createAction(DISMISS_ALERTS_INIT)
export const dismissAlertsComplete = createAction(DISMISS_ALERTS_COMPLETE)
export const updateZoneFlowInit = createAction(UPDATE_ZONE_FLOW_INIT)
export const updateZoneFlowComplete = createAction(UPDATE_ZONE_FLOW_COMPLETE)
export const set = createAction(SET)
export const getWaterJournalInit = createAction(GET_WATER_JOURNAL_INIT)
export const getWaterJournal = createAction(GET_WATER_JOURNAL)
export const getWaterJournalComplete = createAction(GET_WATER_JOURNAL_COMPLETE)
export const setWaterJournalInit = createAction(SET_WATER_JOURNAL_INIT)
export const setWaterJournal = createAction(SET_WATER_JOURNAL)
export const setWaterJournalComplete = createAction(SET_WATER_JOURNAL_COMPLETE)
export const setState = createAction(SET_STATE)
export const startCalibrationInit = createAction(START_CALIBRATION_INIT)
export const startCalibrationComplete = createAction(START_CALIBRATION_COMPLETE)
export const stopCalibrationInit = createAction(STOP_CALIBRATION_INIT)
export const stopCalibrationComplete = createAction(STOP_CALIBRATION_COMPLETE)
export const updateZoneSchedules = createAction(UPDATE_ZONE_SCHEDULES)
export const getWateringSummaryInit = createAction(GET_WATERING_SUMMARY_INIT)
export const getWateringSummaryComplete = createAction(
  GET_WATERING_SUMMARY_COMPLETE
)
export const getZoneWateringSummaryForCSVInit = createAction(
  GET_CSV_WATERING_SUMMARY_INIT
)
export const getZoneWateringSummaryForCSVComplete = createAction(
  GET_CSV_WATERING_SUMMARY_COMPLETE
)

// ACTION REDUCERS ////////////////////////////////////////////////////////////

// $FlowFixMe
export const initialState: ZoneStore = {}

const zone = (state: ZoneStore = initialState, action: Object = {}) => {
  switch (action.type) {
    case START_CALIBRATION_COMPLETE:
      if (action.error) {
        return state // Don't set anything
      } else {
        return {
          ...state,
          [action.payload.zoneId]: {
            ...state[action.payload.zoneId],
            META: {
              ...(state[action.payload.zoneId] || {}).META,
              isCalibrating: true
            }
          }
        }
      }
    case ZONE_CALIBRATION_ENDED_EVENT:
      return {
        ...state,
        [action.payload.zoneId]: {
          ...state[action.payload.zoneId],
          META: {
            ...(state[action.payload.zoneId] || {}).META,
            isCalibrating: false,
            isCalibrationStopped: action.payload.isCalibrationStopped
          }
        }
      }
    case UPDATE_ZONE_SCHEDULES:
      if (action.error) {
        return state
      } else {
        return applyZoneScheduleArrays(action.payload, state)
      }
    case GET_WATERING_SUMMARY_COMPLETE:
      if (action.error) {
        return state
      } else {
        const zone = state[action.payload.zoneId]
        const META = (zone && zone.META) || {}

        return {
          ...state,
          [action.payload.zoneId]: {
            ...zone,
            META: {
              ...META,
              wateringSummary: {
                ...(META.wateringSummary || {}),
                [action.payload.offset]: action.payload.wateringSummary
              }
            }
          }
        }
      }
    case ZONE_GET_COMPLETE:
      if (action.error) {
        return {
          ...state,
          [action.payload.zoneId]: { META: { fetching: action } }
        }
      } else {
        return {
          ...state,
          [action.payload.zoneDetail.id]: {
            META: {
              ...(state[action.payload.zoneDetail.id] || {}).META,
              ...(action.payload.zoneState || {}),
              fetching: false
            },
            ...action.payload.zoneDetail
          }
        }
      }
    case ZONE_UPDATE_COMPLETE:
      if (action.error) {
        return state
      } else {
        return {
          ...state,
          [action.payload.zone.id]: {
            META: {
              ...(state[action.payload.zone.id] || {}).META,
              fetching: false
            },
            ...action.payload.zone
          }
        }
      }
    case UPDATE_ZONE_FLOW_COMPLETE:
      if (action.error) {
        return state
      } else {
        return {
          ...state,
          [action.payload.zone.id]: {
            META: {
              ...(state[action.payload.zone.id] || {}).META,
              fetching: false
            },
            ...action.payload.zone
          }
        }
      }
    case SET:
      if (Array.isArray(action.payload)) {
        return {
          ...state,
          ...action.payload.reduce(
            (newZones, zone) => ({
              ...newZones,
              [zone.id]: {
                META: (state[zone.id] || {}).META || {},
                ...zone
              }
            }),
            {}
          )
        }
      } else {
        return {
          ...state,
          [action.payload.id]: {
            META: (state[action.payload.id] || {}).META || {},
            ...action.payload
          }
        }
      }
    case GET_WATER_JOURNAL:
      return {
        ...state,
        [action.payload.id]: {
          ...state[action.payload.id],
          META: {
            ...((state[action.payload.id] || {}).META || {}),
            waterJournal: action.payload.waterJournal
          }
        }
      }
    case SET_WATER_JOURNAL:
      return {
        ...state,
        [action.payload.zoneId]: {
          ...state[action.payload.zoneId],
          META: {
            ...((state[action.payload.zoneId] || {}).META || {}),
            waterJournal: {
              ...((state[action.payload.zoneId] || {}).META || {}).waterJournal,
              [formatWaterJournalDate(action.payload.localDate)]: {
                // eslint-disable-next-line max-len
                ...(((state[action.payload.zoneId] || {}).META || {})
                  .waterJournal || {})[
                  formatWaterJournalDate(action.payload.localDate)
                ],
                soilMoistureLevelAtEndOfDayPct:
                  action.payload.currentMoisturePct,
                soilMoistureLevelAtEndOfDay:
                  action.payload.currentMoistureLevel,
                irrigations: {
                  FLEX: action.payload.irrigationData.flexRuns,
                  AUTOMATED: action.payload.irrigationData.automatedRuns,
                  MANUAL: action.payload.irrigationData.manualRuns,
                  FILL: action.payload.irrigationData.userEdit
                }
              }
            }
          }
        }
      }
    case SET_STATE:
      return standardMETASet(state, action)
    default:
      return state
  }
}

export default zone
